import { CircleButton } from "@components/circle-button";
import { CarIcon } from "@components/icons/car";
import { CoinsIcon } from "@components/icons/coins";
import { HaircutIcon } from "@components/icons/haircut";
import {
  DropdownItem,
  ResponsiveDropdown,
} from "@components/responsive-dropdown";
import {
  useSuggestionModal,
  SuggestionModal,
} from "@components/suggestion-modal";
import { useImagesWebsockets } from "@features/images/use-images-websockets";
import { useTheme } from "@features/use-theme";
import { useUser } from "@features/user/use-user";
import { formatNumber, useNavigateAlt } from "@features/utils";
import { useIsMobile } from "@features/utils/use-mobile";
import { HomeModernIcon, UserIcon } from "@heroicons/react/16/solid";
import {
  HamburgerMenuIcon,
  LaptopIcon,
  MoonIcon,
  SunIcon,
} from "@radix-ui/react-icons";
import {
  Avatar,
  Badge,
  Button,
  ChevronDownIcon,
  Flex,
  IconButton,
  Separator,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { getPreviousPath } from "./layout";

const useHasScrolled = () => {
  const [hasScrolled, setHasScrolled] = useState(false);

  // Add scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    // Call once to set initial state
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return { hasScrolled };
};

// Supported languages list
const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "es", label: "Español" },
] as const;

// Helper to build language menu items for ResponsiveDropdown
const buildLanguageItems = (
  currentLang: string,
  onChangeLanguage: (lang: string) => void
): DropdownItem[] =>
  SUPPORTED_LANGUAGES.map((lang) => ({
    type: "checkbox" as const,
    label: lang.label,
    checked: currentLang === lang.code,
    onCheckedChange: () => onChangeLanguage(lang.code),
  }));

// Reusable scroll gradient background
const ScrollGradient = ({ hasScrolled }: { hasScrolled: boolean }) => (
  <div
    className={`mobile-safe-header absolute top-0 left-0 z-[-1] pointer-events-none w-full bg-headerlight dark:bg-headerdark transition-opacity duration-200 ${
      hasScrolled ? "opacity-100" : "opacity-0"
    }`}
  >
    <div className="h-24" />
  </div>
);

// User dropdown for logged-in users
const UserDropdown = () => {
  const { i18n, t } = useTranslation();
  const { user, logout } = useUser();
  const navigate = useNavigateAlt();
  const { setMode, mode } = useTheme();

  const dropdownItems: DropdownItem[] = useMemo(
    () => [
      {
        type: "label",
        className: "h-14",
        content: (
          <Flex align="center" className="space-x-2 h-14">
            <Avatar
              size="2"
              src={user.data!.avatar}
              fallback={user.data!.name?.[0] || "U"}
            />
            <Flex direction="column">
              <Text size="2" className="text-black dark:text-white">
                {user.data!.name}
              </Text>
              <Text size="1">{user.data!.email}</Text>
            </Flex>
          </Flex>
        ),
      },
      { type: "separator" },
      {
        type: "item",
        label: t("header.my_models"),
        onClick: () => navigate("/my-models"),
      },
      {
        type: "item",
        label: t("header.my_presets"),
        onClick: () => navigate("/my-presets"),
      },
      { type: "separator" },
      {
        type: "item",
        label: t("header.billing"),
        onClick: () => navigate("/billing"),
      },
      {
        type: "sub",
        label: t("header.language"),
        items: buildLanguageItems(i18n.language, (lang) =>
          i18n.changeLanguage(lang)
        ),
      },
      {
        type: "sub",
        label: t("header.theme") as string,
        items: [
          {
            type: "checkbox" as const,
            label: t("header.theme_tooltip.light") as string,
            checked: mode === "light",
            onCheckedChange: () => setMode("light"),
          },
          {
            type: "checkbox" as const,
            label: t("header.theme_tooltip.dark") as string,
            checked: mode === "dark",
            onCheckedChange: () => setMode("dark"),
          },
        ],
      },
      {
        type: "item",
        label: t("header.logout"),
        onClick: () => logout(),
        color: "red",
      },
    ],
    [user.data, t, navigate, i18n, logout, mode, setMode]
  );

  return (
    <ResponsiveDropdown
      trigger={
        <Flex align="center" className="space-x-2">
          <CircleButton
            icon={(p) => <UserIcon {...p} />}
            src={user.data!.avatar}
          />
          <ChevronDownIcon className="h-2 w-2" />
        </Flex>
      }
      items={dropdownItems}
      title={t("header.account")}
    />
  );
};

// Guest dropdown for logged-out users
const GuestDropdown = () => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigateAlt();

  const dropdownItems: DropdownItem[] = useMemo(
    () => [
      {
        type: "item",
        label: t("header.login"),
        onClick: () => navigate("/"),
      },
      { type: "separator" },
      ...buildLanguageItems(i18n.language, (lang) => i18n.changeLanguage(lang)),
    ],
    [t, navigate, i18n]
  );

  return (
    <ResponsiveDropdown
      trigger={
        <Flex align="center" className="space-x-2">
          <CircleButton icon={(p) => <HamburgerMenuIcon {...p} />} />
          <ChevronDownIcon className="h-2 w-2" />
        </Flex>
      }
      items={dropdownItems}
      title={t("header.menu")}
    />
  );
};

const Header = ({ withSites }: { withSites?: boolean }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  useImagesWebsockets();
  const navigate = useNavigateAlt();
  const { setMode, mode } = useTheme();
  const { site } = useParams();
  const { hasScrolled } = useHasScrolled();
  const {
    isOpen: suggestionModalOpen,
    openModal: openSuggestionModal,
    closeModal: closeSuggestionModal,
  } = useSuggestionModal();

  return (
    <header className="mobile-safe-header sticky top-0 z-40 relative">
      <SuggestionModal
        isOpen={suggestionModalOpen}
        onClose={closeSuggestionModal}
        forceOpen
      />
      <div className="w-full z-20 px-4 py-2 flex justify-between items-center">
        {/* Left Side */}
        <LogoOrBack />
        {withSites && (
          <>
            <Separator
              orientation="vertical"
              className="hidden md:block mx-4"
            />
            <div className="flex items-center space-x-2 hidden md:inline">
              <Button
                size="1"
                color={site === "portraits" ? "iris" : "gray"}
                variant={site === "portraits" ? "solid" : "surface"}
                onClick={() => navigate(`/portraits/home`)}
              >
                <HaircutIcon className="h-4 w-4" />
                <span className="hidden md:inline">Portraits</span>
              </Button>
              {false && (
                <Button
                  size="1"
                  color={site === "architect" ? "grass" : "gray"}
                  variant={site === "architect" ? "solid" : "surface"}
                  onClick={() => navigate(`/architect/home`)}
                >
                  <HomeModernIcon className="h-4 w-4" />
                  <span className="hidden md:inline">Architect</span>
                </Button>
              )}
              <Button
                size="1"
                color={site === "showroom" ? "amber" : "gray"}
                variant={site === "showroom" ? "solid" : "surface"}
                onClick={() => navigate(`/showroom/home`)}
              >
                <CarIcon className="h-4 w-4" />
                <span className="hidden md:inline">Showroom</span>
              </Button>
            </div>
          </>
        )}

        {/* Right Side */}
        <div className="w-1/2 grow flex justify-end items-center space-x-2 md:space-x-4">
          <div className="space-x-2 flex items-center hidden md:block">
            {/* Feature Request */}
            <Button size="1" variant="soft" onClick={openSuggestionModal}>
              {t("header.feedback")}
            </Button>

            <a
              href="https://photographeai.featurebase.app/"
              target="_blank"
              rel="noreferrer"
            >
              <Button size="1" variant="soft">
                {t("header.manuals")}
              </Button>
            </a>

            {/* Theme */}
            <Tooltip
              content={t(
                "header.theme_tooltip." +
                  (mode === "dark"
                    ? "dark"
                    : mode === "light"
                    ? "light"
                    : "auto")
              )}
            >
              <IconButton
                size="1"
                variant="soft"
                className="m-0"
                onClick={() => setMode(mode === "dark" ? "light" : "dark")}
              >
                {mode && (
                  <>
                    <MoonIcon className="h-4 w-4 dark:block hidden" />
                    <SunIcon className="h-4 w-4 dark:hidden block" />
                  </>
                )}
                {!mode && <LaptopIcon className="h-4 w-4 block" />}
              </IconButton>
            </Tooltip>
          </div>

          <Separator orientation="vertical" className="hidden md:block" />

          <Tooltip content={t("header.credits_tooltip")}>
            <Badge
              size="2"
              variant="soft"
              color={
                (user.data!.credits || 0) +
                  (user.data?.additional_credits || 0) >
                500
                  ? undefined
                  : user.data!.credits > 100
                  ? "orange"
                  : "red"
              }
              className="cursor-pointer"
              onClick={(event: any) => navigate("/billing", event)}
            >
              <Flex align="center" className={twMerge("space-x-1")}>
                <Text>
                  {formatNumber(
                    (user.data!.credits || 0) +
                      (user.data!.additional_credits || 0)
                  )}
                  <CoinsIcon className="h-4 w-4 inline-block ml-1" />
                </Text>
              </Flex>
            </Badge>
          </Tooltip>
          <Separator orientation="vertical" className="hidden md:block" />

          {/* User Avatar and Dropdown */}
          <UserDropdown />
        </div>
      </div>
      <ScrollGradient hasScrolled={hasScrolled} />
    </header>
  );
};

export const LogoutHeader = () => {
  const { setMode, mode } = useTheme();
  const { t } = useTranslation();
  const { hasScrolled } = useHasScrolled();
  const { user } = useUser();

  if (user?.data?.id) {
    return <Header />;
  }

  return (
    <header className="mobile-safe-header sticky top-0 z-40 relative">
      <div className="w-full z-20 px-4 py-2 flex justify-between items-center">
        {/* Left Side */}
        <LogoOrBack />

        {/* Right Side */}
        <div className="w-1/2 grow flex justify-end items-center space-x-2 md:space-x-4">
          <div className="space-x-2 flex items-center hidden md:block">
            {/* Theme */}
            <Tooltip
              content={t(
                "header.theme_tooltip." +
                  (mode === "dark"
                    ? "dark"
                    : mode === "light"
                    ? "light"
                    : "auto")
              )}
            >
              <IconButton
                size="1"
                variant="soft"
                className="m-0"
                onClick={() => setMode(mode === "dark" ? "light" : "dark")}
              >
                {mode && (
                  <>
                    <MoonIcon className="h-4 w-4 dark:block hidden" />
                    <SunIcon className="h-4 w-4 dark:hidden block" />
                  </>
                )}
                {!mode && <LaptopIcon className="h-4 w-4 block" />}
              </IconButton>
            </Tooltip>
          </div>

          {/* User Avatar and Dropdown */}
          <GuestDropdown />
        </div>
      </div>
      <ScrollGradient hasScrolled={hasScrolled} />
    </header>
  );
};

export default Header;

let timeout1: any = 0;
let timeout2: any = 0;
let timeout3: any = 0;

export const FollowingLogo = () => {
  const logo = useRef<HTMLDivElement>(null);
  const flash = useRef<HTMLDivElement>(null);

  // When we click (not every time, one out of 4 random times) on the logo, the logo will point to the cursor in 0.2s, wait 0.5s and then go back to its original position
  const handleClick = (event: any) => {
    if (Math.random() > 0.2) return;

    clearTimeout(timeout1);
    clearTimeout(timeout2);
    clearTimeout(timeout3);
    if (logo.current && flash.current) {
      // Get cursor position
      const { clientX, clientY } = event!;
      // Get logo position
      const { x, y } = logo.current.getBoundingClientRect();
      // Get logo angle (default if facing right)
      let angle = 0;
      // Compute angle using atan2
      angle = (Math.atan2(clientY - y, clientX - x) * 180) / Math.PI;
      // Set logo to point to cursor
      logo.current.style.transition = "transform 0.2s";
      logo.current.style.transform = `rotate(${angle}deg)`;
      flash.current!.style.transition = "all 0.2s";
      flash.current!.style.opacity = "0";
      flash.current!.style.transform = "scale(1)";

      // After 0.2s, show flash
      timeout1 = setTimeout(() => {
        if (flash.current) {
          flash.current!.style.transition = "all 0s";
          flash.current!.style.opacity = "1";
          flash.current!.style.transform = "scale(2)";
        }
      }, 200);
      timeout2 = setTimeout(() => {
        if (flash.current) {
          flash.current!.style.transition = "all 0.2s";
          flash.current!.style.opacity = "0";
          flash.current!.style.transform = "scale(1)";
        }
      }, 250);

      // After 0.5s, go back to original position
      timeout3 = setTimeout(() => {
        if (!logo.current) return;
        logo.current!.style.transition = "transform 0.2s";
        logo.current!.style.transform = "rotate(0deg)";
      }, 500);
    }
  };

  useEffect(() => {
    window.addEventListener("click", handleClick);
    return () => {
      window.removeEventListener("click", handleClick);
    };
  }, []);

  return (
    <div ref={logo} className="relative">
      <img
        src="/logo.svg"
        alt="photographe.ai"
        className="w-4 h-4 block dark:hidden"
      />
      <img
        src="/logo-white.svg"
        alt="photographe.ai"
        className="w-4 h-4 hidden dark:block"
      />
      <div
        style={{ opacity: 0 }}
        className="bg-white w-4 h-4 absolute top-0 -right-2 rounded-full blur-sm"
        ref={flash}
      />
    </div>
  );
};

const LogoOrBack = () => {
  const { pathname } = useLocation();
  const { site } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigateAlt();
  const isMobile = useIsMobile();

  const isHome =
    pathname === "/" ||
    pathname.includes("/logout") ||
    pathname.includes("/welcome") ||
    pathname === `/${site}/` ||
    pathname === `/${site}/home`;

  // Function to handle navigation and close the studio modal
  const handleHomeNavigation = (e: React.MouseEvent) => {
    navigate("/" + site + "/", e);
  };

  if (!isMobile) {
    return (
      <div className="flex items-center space-x-2 shrink-0">
        <FollowingLogo />

        {/* Title */}
        <Text
          size="5"
          className="font-bold cursor-pointer md:text-base text-sm"
          onClick={handleHomeNavigation}
        >
          Photographe
          <Text
            size="1"
            className="font-bold cursor-pointer md:text-xs text-2xs"
          >
            .ai
          </Text>
        </Text>
      </div>
    );
  }

  return (
    <div className="flex items-center">
      <AnimatePresence mode="wait">
        {isHome ? (
          <motion.div
            key="logo"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="flex items-center space-x-2 shrink-0"
          >
            <FollowingLogo />

            {/* Title */}
            <Text
              size="5"
              className="font-bold cursor-pointer md:text-base text-sm"
              onClick={handleHomeNavigation}
            >
              Photographe
              <Text
                size="1"
                className="font-bold cursor-pointer md:text-xs text-2xs"
              >
                .ai
              </Text>
            </Text>
          </motion.div>
        ) : (
          <>
            <motion.div
              key="logo-desktop"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="hidden md:flex items-center space-x-2 shrink-0"
            >
              <FollowingLogo />

              {/* Title */}
              <Text
                size="5"
                className="font-bold cursor-pointer md:text-base text-sm"
                onClick={handleHomeNavigation}
              >
                Photographe
                <Text
                  size="1"
                  className="font-bold cursor-pointer md:text-xs text-2xs"
                >
                  .ai
                </Text>
              </Text>
            </motion.div>
            <motion.div
              key="back"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              className="ml-1 md:ml-4 md:hidden block"
            >
              <Link
                to={getPreviousPath()}
                className="flex items-center space-x-1 -ml-1"
              >
                <Button size="2" className="flex items-center">
                  <ChevronLeft className="h-4 w-4 -mx-1" />
                  {t("welcome.back")}
                </Button>
              </Link>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
